import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import { resolveUrl } from "@/lib/api";

function hostname(url) {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

export function BrandCard({ brand, index }) {
  const isValidHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(brand.accent_color || "");
  const accent = isValidHex ? brand.accent_color : "#292524";
  const hasLink = !!brand.website_url;

  const inner = (
    <>
      <div
        className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      />
      <div className="flex h-20 items-center justify-start mb-6">
        {brand.logo_url ? (
          <img
            src={resolveUrl(brand.logo_url)}
            alt={`${brand.brand_name} logo`}
            className="h-14 w-auto max-w-full object-contain object-left mix-blend-multiply"
            loading="lazy"
          />
        ) : (
          <div
            className="flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-heading font-semibold text-white"
            style={{ backgroundColor: accent }}
          >
            {brand.brand_name.charAt(0)}
          </div>
        )}
      </div>

      <h3 className="font-heading text-2xl font-semibold tracking-tight text-stone-900 mb-3">
        {brand.brand_name}
      </h3>
      <p className="text-sm leading-relaxed text-stone-600 mb-8 flex-1">
        {brand.description}
      </p>

      <div
        className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
        style={{ color: hasLink ? accent : "#a8a29e" }}
        data-testid={`brand-cta-${brand.id}`}
      >
        {hasLink ? (
          <>
            Visit Website
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-stone-400">
            Coming soon
          </span>
        )}
      </div>
    </>
  );

  const cardClasses =
    "group relative flex h-full flex-col rounded-2xl border border-stone-200/70 bg-white p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)] transition-[transform,box-shadow] duration-300 ease-out";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="h-full"
    >
      {hasLink ? (
        <a
          href={brand.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${cardClasses} hover:-translate-y-1.5 hover:shadow-[0_14px_34px_rgb(0,0,0,0.09)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2`}
          data-testid={`brand-card-${brand.id}`}
          title={`Visit ${brand.brand_name} (opens in a new tab)`}
        >
          {inner}
          <ExternalLink className="absolute right-6 top-6 h-4 w-4 text-stone-300 opacity-0 transition-opacity group-hover:opacity-100" />
        </a>
      ) : (
        <div className={cardClasses} data-testid={`brand-card-${brand.id}`}>
          {inner}
        </div>
      )}
    </motion.div>
  );
}
